import {
  apyToApr,
  calculateRateForCompoundType,
  TimeScale,
} from '../../../../utils/parsing'

/**
 * Raw MarketData struct returned by TectonicLens.getMarketsData().
 */
export interface TectonicMarketData {
  tokenAddress: string // underlying address
  label: string
  symbol: string
  decimal: number // underlying decimals
  ttokenAddress: string // cToken address
  ttokenDecimal: number // cToken decimals
  borrowCap: bigint
  supplyCap: bigint
  availableLiquidity: bigint // getCash
  asCollateralEnabled: boolean
  reserves: bigint
  reserveFactor: bigint // 1e18 scaled
  collateralFactor: bigint // 1e18 scaled
  liquidationPenalty: bigint // 1e18 scaled
  price: bigint // oracle price, 1e(36 - decimal) scaled
  exchangeRate: bigint // 1e(10 + decimal) scaled for 8-dec tTokens
  tTokenMinted: bigint // tToken total supply
  totalSupply: bigint // same as tTokenMinted
  totalBorrow: bigint // underlying total borrows
  borrowRatePerBlock: bigint // 1e18 scaled
  supplyRatePerBlock: bigint // 1e18 scaled
  availableBorrow: bigint
  isBoosted: boolean
  baseRatePerBlock: bigint
  multiplierPerBlock: bigint
  jumpMultiplierPerBlock: bigint
  kink: bigint
}

function toBigInt(value: unknown): bigint {
  if (typeof value === 'bigint') return value
  if (typeof value === 'number') return BigInt(value)
  if (typeof value === 'string') return BigInt(value)
  if (value && typeof value === 'object') {
    const v = value as any
    if (typeof v._hex === 'string') return BigInt(v._hex)
    if (typeof v.toString === 'function') return BigInt(v.toString())
  }
  throw new Error(`Cannot convert value to bigint: ${String(value)}`)
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'bigint') return value !== 0n
  if (typeof value === 'number') return value !== 0
  return Boolean(value)
}

function field<T = unknown>(src: any, key: string, index: number): T {
  if (src == null) throw new Error('Source is null/undefined')
  if (key in src) return src[key]
  if (Array.isArray(src)) return src[index]
  return (src as any)[key]
}

export function parseTectonicMarketData(input: any): TectonicMarketData {
  return {
    tokenAddress: String(field(input, 'tokenAddress', 0)),
    label: String(field(input, 'label', 1)),
    symbol: String(field(input, 'symbol', 2)),
    decimal: Number(field(input, 'decimal', 3)),
    ttokenAddress: String(field(input, 'ttokenAddress', 4)),
    ttokenDecimal: Number(field(input, 'ttokenDecimal', 5)),
    borrowCap: toBigInt(field(input, 'borrowCap', 6)),
    supplyCap: toBigInt(field(input, 'supplyCap', 7)),
    availableLiquidity: toBigInt(field(input, 'availableLiquidity', 8)),
    asCollateralEnabled: toBool(field(input, 'asCollateralEnabled', 9)),
    reserves: toBigInt(field(input, 'reserves', 10)),
    reserveFactor: toBigInt(field(input, 'reserveFactor', 11)),
    collateralFactor: toBigInt(field(input, 'collateralFactor', 12)),
    liquidationPenalty: toBigInt(field(input, 'liquidationPenalty', 13)),
    price: toBigInt(field(input, 'price', 14)),
    exchangeRate: toBigInt(field(input, 'exchangeRate', 15)),
    tTokenMinted: toBigInt(field(input, 'tTokenMinted', 16)),
    totalSupply: toBigInt(field(input, 'totalSupply', 17)),
    totalBorrow: toBigInt(field(input, 'totalBorrow', 18)),
    borrowRatePerBlock: toBigInt(field(input, 'borrowRatePerBlock', 19)),
    supplyRatePerBlock: toBigInt(field(input, 'supplyRatePerBlock', 20)),
    availableBorrow: toBigInt(field(input, 'availableBorrow', 21)),
    isBoosted: toBool(field(input, 'isBoosted', 22)),
    baseRatePerBlock: toBigInt(field(input, 'baseRatePerBlock', 23)),
    multiplierPerBlock: toBigInt(field(input, 'multiplierPerBlock', 24)),
    jumpMultiplierPerBlock: toBigInt(
      field(input, 'jumpMultiplierPerBlock', 25),
    ),
    kink: toBigInt(field(input, 'kink', 26)),
  }
}

/**
 * Returns deposit APR and borrow APR (percentages) for one Tectonic market.
 * Rates are per-block; we use the chain block time to compute APY then convert to APR.
 */
export function getTectonicRates(
  info: TectonicMarketData,
  chainId: string,
  unitSeconds?: number,
) {
  const supplyApy = calculateRateForCompoundType(
    info.supplyRatePerBlock.toString(),
    chainId,
    TimeScale.BLOCK,
    unitSeconds,
  )
  const borrowApy = calculateRateForCompoundType(
    info.borrowRatePerBlock.toString(),
    chainId,
    TimeScale.BLOCK,
    unitSeconds,
  )
  return {
    depositRate: apyToApr(supplyApy / 100) * 100,
    variableBorrowRate: apyToApr(borrowApy / 100) * 100,
  }
}
